import { useState, useEffect } from 'react';
import {
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Play,
  Pause,
  FileText,
  Mic,
  ExternalLink,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { PipelineStatus, SyncLog } from '../types';
import { formatRelativeTime, getStatusLabel } from '../utils/format';
import { api } from '../api/client';

export default function SyncDashboard() {
  const [status, setStatus] = useState<PipelineStatus | null>(null);
  const [logs, setLogs] = useState<SyncLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadData = async () => {
    try {
      const [statusData, logsData] = await Promise.all([
        api.getSyncStatus(),
        api.getRecentSyncLogs(20),
      ]);
      setStatus(statusData);
      setLogs(logsData);
    } catch (err) {
      console.error('Failed to load sync status:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const handleAction = async (action: string) => {
    setActionLoading(action);
    try {
      switch (action) {
        case 'transcribe-all':
          await api.transcribeAll();
          break;
        case 'evaluate-all':
          await api.evaluateAll();
          break;
        case 'sync-all':
          await api.syncAll();
          break;
      }
      await loadData();
    } catch (err) {
      console.error(`Action ${action} failed:`, err);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusIcon = (logStatus: string) => {
    switch (logStatus) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'in_progress':
        return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'skipped':
        return <Pause className="w-4 h-4 text-gray-400" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getStepIcon = (step: string) => {
    if (step.includes('transcri')) return <Mic className="w-4 h-4" />;
    if (step.includes('evaluat')) return <FileText className="w-4 h-4" />;
    if (step.includes('hubspot') || step.includes('sync'))
      return <ExternalLink className="w-4 h-4" />;
    return <Play className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-500">Lade Sync Status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-medium text-gray-900">Pipeline Status</h2>
        <button
          onClick={loadData}
          className="btn-secondary"
          disabled={loading}
        >
          <RefreshCw className={clsx('w-4 h-4 mr-2', loading && 'animate-spin')} />
          Aktualisieren
        </button>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Pending Transcription */}
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Mic className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Warten auf Transkription</p>
                <p className="text-2xl font-bold text-gray-900">
                  {status?.pending_transcription ?? 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Pending Evaluation */}
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Warten auf Bewertung</p>
                <p className="text-2xl font-bold text-gray-900">
                  {status?.pending_evaluation ?? 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Pending Sync */}
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <ExternalLink className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Warten auf HubSpot Sync</p>
                <p className="text-2xl font-bold text-gray-900">
                  {status?.pending_sync ?? 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 mb-6">
        <button
          onClick={() => handleAction('transcribe-all')}
          disabled={actionLoading !== null || (status?.pending_transcription ?? 0) === 0}
          className="btn-primary"
        >
          {actionLoading === 'transcribe-all' ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Mic className="w-4 h-4 mr-2" />
          )}
          Alle transkribieren ({status?.pending_transcription ?? 0})
        </button>

        <button
          onClick={() => handleAction('evaluate-all')}
          disabled={actionLoading !== null || (status?.pending_evaluation ?? 0) === 0}
          className="btn-primary"
        >
          {actionLoading === 'evaluate-all' ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <FileText className="w-4 h-4 mr-2" />
          )}
          Alle bewerten ({status?.pending_evaluation ?? 0})
        </button>

        <button
          onClick={() => handleAction('sync-all')}
          disabled={actionLoading !== null || (status?.pending_sync ?? 0) === 0}
          className="btn-primary"
        >
          {actionLoading === 'sync-all' ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <ExternalLink className="w-4 h-4 mr-2" />
          )}
          Alle synchronisieren ({status?.pending_sync ?? 0})
        </button>
      </div>

      {/* Activity Timeline */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Letzte Aktivitaten</h3>
        </div>

        {logs.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            Keine Aktivitaten vorhanden
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {logs.map((log) => (
              <div key={log.id} className="px-6 py-4 flex items-start space-x-4">
                {/* Status Icon */}
                <div className="flex-shrink-0 mt-0.5">
                  {getStatusIcon(log.status)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-400">
                      {getStepIcon(log.step)}
                    </span>
                    <span className="text-sm font-medium text-gray-900 capitalize">
                      {log.step.replace(/_/g, ' ')}
                    </span>
                    <span
                      className={clsx('badge text-xs', {
                        'badge-green': log.status === 'completed',
                        'badge-red': log.status === 'error',
                        'badge-yellow':
                          log.status === 'pending' || log.status === 'in_progress',
                        'badge-gray': log.status === 'skipped',
                      })}
                    >
                      {getStatusLabel(log.status)}
                    </span>
                  </div>

                  {log.message && (
                    <p className="mt-1 text-sm text-gray-600">{log.message}</p>
                  )}

                  {log.error_details && (
                    <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-600 flex items-start space-x-2">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>{log.error_details}</span>
                    </div>
                  )}

                  <p className="mt-1 text-xs text-gray-400">
                    {formatRelativeTime(log.created_at)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
